import { Fragment, useEffect, useMemo, useState } from 'react';
import { recurringService } from '../../services/api-secure';
import { Spinner } from '../Spinner';
import { calmTokens as t } from '../../shared/tokens';
import { IconPlus, IconRepeat, IconX } from '../../shared/icons';
import CalmToast from '../shared/CalmToast';
import { templateId, RECURRENCE_KINDS } from '../../shared/recurrence';
import SettingsCalendarCell from './SettingsCalendarCell';
import SettingsTplCard from './SettingsTplCard';
import SettingsTemplateModal from './SettingsTemplateModal';
import SettingsDeleteModal from './SettingsDeleteModal';
import BankImportSection from './BankImportSection';

function RecurrenceLegend() {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        alignItems: 'center',
      }}
      aria-label="Légende des types de récurrence"
    >
      {Object.entries(RECURRENCE_KINDS).map(([key, kind]) => (
        <div
          key={key}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '5px 12px 5px 10px',
            borderRadius: 999,
            background: kind.bg,
            border: `1px solid ${kind.border}`,
            borderLeft: `3px solid ${kind.color}`,
            fontFamily: t.fontUi,
            lineHeight: 1,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: kind.color,
              letterSpacing: -0.1,
            }}
          >
            {kind.label}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function SettingsView({ userId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [operating, setOperating] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | null
  const [modalTemplate, setModalTemplate] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  const [feedback, setFeedback] = useState({ message: '', type: '', visible: false });

  useEffect(() => {
    if (userId) loadTemplates({ silent: false });
  }, [userId]);

  const loadTemplates = async ({ silent = true } = {}) => {
    if (!silent) setLoading(true);
    try {
      const list = await recurringService.getTemplates();
      setTemplates(list);
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]);
      showFeedback('Impossible de charger les récurrentes.', 'error');
    }
    if (!silent) setLoading(false);
  };

  const showFeedback = (message, type = 'info') => {
    setFeedback({ message, type, visible: true });
    if (type !== 'error') {
      setTimeout(() => {
        setFeedback((f) => ({ ...f, visible: false }));
      }, 4000);
    }
  };

  const activeTemplates = useMemo(
    () => templates.filter((tpl) => tpl.isActive),
    [templates]
  );

  const inactiveCount = templates.length - activeTemplates.length;

  const byDay = useMemo(() => {
    const g = {};
    for (let d = 1; d <= 31; d++) g[d] = [];
    activeTemplates.forEach((tpl) => {
      const d = Number(tpl.dayOfMonth);
      if (d >= 1 && d <= 31) g[d].push(tpl);
    });
    return g;
  }, [activeTemplates]);

  const openCreate = () => {
    setModalTemplate(null);
    setModalMode('create');
  };

  const openEdit = (tpl) => {
    setModalTemplate(tpl);
    setModalMode('edit');
  };

  const closeModal = () => {
    if (operating) return;
    setModalMode(null);
    setModalTemplate(null);
  };

  const handleSave = async (payload) => {
    setOperating(true);
    try {
      if (modalMode === 'edit' && modalTemplate) {
        await recurringService.updateTemplate(templateId(modalTemplate), payload);
        showFeedback('Récurrente modifiée.', 'success');
      } else {
        await recurringService.createTemplate(payload);
        showFeedback('Récurrente créée.', 'success');
      }
      setModalMode(null);
      setModalTemplate(null);
      await loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      showFeedback(
        modalMode === 'edit'
          ? 'Erreur lors de la modification.'
          : 'Erreur lors de la création.',
        'error'
      );
    }
    setOperating(false);
  };

  const handleToggle = async (tpl) => {
    setOperating(true);
    try {
      await recurringService.toggleTemplate(templateId(tpl));
      await loadTemplates();
    } catch (error) {
      console.error('Error toggling template:', error);
      showFeedback("Impossible de changer l'état.", 'error');
    }
    setOperating(false);
  };

  const requestDelete = (tpl) => {
    setDeletingTemplate(tpl);
  };

  const confirmDelete = async () => {
    if (!deletingTemplate) return;
    setOperating(true);
    try {
      await recurringService.deleteTemplate(templateId(deletingTemplate));
      setDeletingTemplate(null);
      setModalMode(null);
      setModalTemplate(null);
      showFeedback('Récurrente supprimée.', 'success');
      await loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      showFeedback('Erreur lors de la suppression.', 'error');
    }
    setOperating(false);
  };

  const handleDeleteFromModal = () => {
    if (!modalTemplate) return;
    setDeletingTemplate(modalTemplate);
  };

  const selectedDayTpls = selectedDay ? byDay[selectedDay] || [] : [];

  if (loading) {
    return (
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          padding: 60,
          color: t.textDim,
        }}
      >
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  return (
    <div style={{ color: t.text, fontFamily: t.fontUi }}>
      <CalmToast
        feedback={feedback}
        onClose={() => setFeedback((f) => ({ ...f, visible: false }))}
      />

      {/* Hero */}
      <div
        className="settings-hero"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 24,
          marginBottom: 28,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 13,
              color: t.textDim,
              marginBottom: 10,
            }}
          >
            <IconRepeat size={13} style={{ color: t.accent }} />
            <span>Paramétrage · calendrier perpétuel</span>
          </div>
          <h1
            style={{
              fontFamily: t.fontDisp,
              fontSize: 'clamp(26px, 4vw, 36px)',
              fontWeight: 400,
              letterSpacing: -1,
              margin: 0,
              lineHeight: 1.15,
              color: t.text,
            }}
          >
            Ce qui reviendra{' '}
            <span style={{ fontStyle: 'italic', color: t.accent }}>
              chaque mois
            </span>
            .
          </h1>
          <p
            style={{
              fontSize: 14,
              color: t.textDim,
              margin: '10px 0 0',
              letterSpacing: -0.1,
              maxWidth: 560,
            }}
          >
            Chaque jour du mois où une opération récurrente tombe, visualisée d&apos;un
            coup d&apos;œil.
          </p>
        </div>

        <button
          type="button"
          onClick={openCreate}
          style={{
            padding: '11px 18px',
            background: t.text,
            color: t.bg,
            border: 'none',
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: t.fontUi,
            letterSpacing: -0.1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <IconPlus size={14} />
          Nouvelle récurrente
        </button>
      </div>

      {/* Compteur discret */}
      <div
        className="settings-counter"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 18,
          padding: '0 4px',
          fontSize: 12,
          color: t.textDim,
          fontFamily: t.fontUi,
          flexWrap: 'wrap',
        }}
      >
        <span>
          <span style={{ color: t.text, fontFamily: t.fontMono, fontWeight: 500 }}>
            {activeTemplates.length}
          </span>{' '}
          récurrente{activeTemplates.length > 1 ? 's' : ''} active
          {activeTemplates.length > 1 ? 's' : ''}
        </span>
        {inactiveCount > 0 && (
          <>
            <span style={{ opacity: 0.4 }}>·</span>
            <span>
              <span style={{ color: t.textMute, fontFamily: t.fontMono }}>
                {inactiveCount}
              </span>{' '}
              désactivée{inactiveCount > 1 ? 's' : ''}
            </span>
          </>
        )}
      </div>

      {/* Calendrier 31 jours */}
      <div
        style={{
          background: t.surface,
          border: `1px solid ${t.border}`,
          borderRadius: 18,
          padding: 20,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 16,
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          <h2
            style={{
              fontFamily: t.fontDisp,
              fontSize: 18,
              fontWeight: 500,
              margin: 0,
              letterSpacing: -0.3,
              color: t.text,
            }}
          >
            Jours du mois
          </h2>
          <RecurrenceLegend />
        </div>

        {activeTemplates.length === 0 ? (
          <div
            style={{
              padding: '32px 20px',
              textAlign: 'center',
              color: t.textDim,
              fontSize: 13,
              border: `1px dashed ${t.border}`,
              borderRadius: 12,
            }}
          >
            Aucune récurrente active. Ajoutez-en une pour voir votre mois
            se dessiner.
          </div>
        ) : (
          <div
            className="settings-calendar-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 8,
            }}
          >
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
              <SettingsCalendarCell
                key={d}
                day={d}
                tpls={byDay[d]}
                active={selectedDay === d}
                onSelect={(day) =>
                  setSelectedDay((curr) => (curr === day ? null : day))
                }
              />
            ))}
          </div>
        )}

        {selectedDay && selectedDayTpls.length > 0 && (
          <div
            className="settings-day-detail"
            style={{
              marginTop: 18,
              background: 'rgba(0,0,0,0.25)',
              border: `1px solid ${t.border}`,
              borderRadius: 14,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 20px',
                borderBottom: `1px solid ${t.border}`,
                gap: 10,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 10,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    fontFamily: t.fontDisp,
                    fontSize: 16,
                    fontWeight: 500,
                    color: t.text,
                    letterSpacing: -0.3,
                  }}
                >
                  Jour{' '}
                  <span style={{ fontFamily: t.fontMono, color: t.accent }}>
                    {String(selectedDay).padStart(2, '0')}
                  </span>
                </span>
                <span style={{ fontSize: 12, color: t.textMute }}>
                  {selectedDayTpls.length} récurrente
                  {selectedDayTpls.length > 1 ? 's' : ''}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setSelectedDay(null)}
                aria-label="Fermer le détail du jour"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: 'transparent',
                  border: `1px solid ${t.border}`,
                  color: t.textMute,
                  cursor: 'pointer',
                  display: 'grid',
                  placeItems: 'center',
                  padding: 0,
                }}
              >
                <IconX size={12} />
              </button>
            </div>
            {selectedDayTpls.map((tpl, i) => (
              <Fragment key={templateId(tpl)}>
                {i > 0 && (
                  <div
                    style={{
                      height: 1,
                      background: t.border,
                      margin: '0 20px',
                    }}
                  />
                )}
                <SettingsTplCard
                  tpl={tpl}
                  onEdit={() => openEdit(tpl)}
                  onToggle={() => handleToggle(tpl)}
                  onDelete={() => requestDelete(tpl)}
                  showDay={false}
                />
              </Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Désactivées */}
      {inactiveCount > 0 && (
        <div style={{ marginTop: 8 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 12,
              padding: '0 4px',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <h2
              style={{
                fontFamily: t.fontDisp,
                fontSize: 18,
                fontWeight: 500,
                margin: 0,
                letterSpacing: -0.3,
                color: t.text,
              }}
            >
              Désactivées
            </h2>
            <span style={{ fontSize: 11, color: t.textMute }}>
              Non affichées dans le calendrier
            </span>
          </div>

          <div
            style={{
              background: t.surface,
              border: `1px solid ${t.border}`,
              borderRadius: 16,
              overflow: 'hidden',
            }}
          >
            {templates
              .filter((tpl) => !tpl.isActive)
              .map((tpl, i) => (
                <Fragment key={templateId(tpl)}>
                  {i > 0 && (
                    <div
                      style={{
                        height: 1,
                        background: t.border,
                        margin: '0 20px',
                      }}
                    />
                  )}
                  <SettingsTplCard
                    tpl={tpl}
                    onEdit={() => openEdit(tpl)}
                    onToggle={() => handleToggle(tpl)}
                    onDelete={() => requestDelete(tpl)}
                  />
                </Fragment>
              ))}
          </div>
        </div>
      )}

      {/* Empty state global */}
      {templates.length === 0 && (
        <div
          style={{
            marginTop: 16,
            padding: '40px 24px',
            background: t.surface,
            border: `1px solid ${t.border}`,
            borderRadius: 16,
            textAlign: 'center',
            color: t.textDim,
          }}
        >
          <div
            style={{
              fontFamily: t.fontDisp,
              fontSize: 20,
              fontWeight: 500,
              letterSpacing: -0.3,
              color: t.text,
              marginBottom: 6,
            }}
          >
            Aucune récurrente enregistrée
          </div>
          <div style={{ fontSize: 13 }}>
            Commencez par ajouter un salaire, un loyer ou un abonnement.
          </div>
        </div>
      )}

      <BankImportSection showFeedback={showFeedback} />

      <SettingsTemplateModal
        open={modalMode !== null}
        mode={modalMode || 'create'}
        template={modalTemplate}
        onClose={closeModal}
        onSave={handleSave}
        onDelete={modalMode === 'edit' ? handleDeleteFromModal : undefined}
        loading={operating}
      />

      <SettingsDeleteModal
        open={Boolean(deletingTemplate)}
        template={deletingTemplate}
        onConfirm={confirmDelete}
        onClose={() => {
          if (!operating) setDeletingTemplate(null);
        }}
        loading={operating}
      />

      {operating && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 90,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              background: t.surface,
              border: `1px solid ${t.borderS}`,
              borderRadius: 14,
              padding: '12px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              color: t.text,
              fontFamily: t.fontUi,
              fontSize: 13,
            }}
          >
            <Spinner className="w-5 h-5" />
            <span>En cours…</span>
          </div>
        </div>
      )}

      <style>{`
        .settings-cell-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .settings-cell-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .settings-cell-scroll::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.08);
          border-radius: 4px;
        }
        .settings-cell-scroll:hover::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.18);
        }
        @media (max-width: 900px) {
          .settings-calendar-grid {
            grid-template-columns: repeat(5, minmax(0, 1fr)) !important;
          }
        }
        @media (max-width: 560px) {
          .settings-calendar-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          }
        }
      `}</style>
    </div>
  );
}
